import { type ComputedRef, type Ref, computed, ref, watch } from 'vue'
import { isAxiosError } from 'axios'
import { isChatMatrixEnabled } from '@/environment/environment'
import { ChatApiService } from '@/modules/chat-matrix-fast/services/chatApiService'
import type { EventChatAppLabel, EventChatModelName } from '@/modules/chat-matrix-fast/types/eventChatEntity'
import { openChat } from '@/utils/openChat'
import { debugConsoleError } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

const ACCESS_DENIED_MESSAGE = 'У вас нет доступа к чату'

export interface IUseEntityChatParams {
  appLabel: EventChatAppLabel
  modelName: EventChatModelName
  objectId: Ref<number | null | undefined> | ComputedRef<number | null | undefined>
  isEnabled: Ref<boolean> | ComputedRef<boolean>
}

export interface IUseEntityChatReturn {
  isAvailable: ComputedRef<boolean>
  chatId: Ref<number | null>
  connectError: Ref<string | null>
  isChatVisible: Ref<boolean>
  hideChat: () => void
  showChat: () => void
  openInSlidingChat: () => Promise<void>
}

function isForbiddenError(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 403
}

export function useEntityChat(params: IUseEntityChatParams): IUseEntityChatReturn {
  const chatId = ref<number | null>(null)
  const connectError = ref<string | null>(null)
  const isChatVisible = ref(true)
  let attemptedFor: number | null = null
  let ensureGeneration = 0

  const isAvailable = computed(() => {
    if (!isChatMatrixEnabled() || !params.isEnabled.value) {
      return false
    }

    const id = params.objectId.value
    return typeof id === 'number' && Number.isFinite(id)
  })

  async function ensureChat() {
    if (!isAvailable.value) {
      return
    }

    const id = params.objectId.value
    if (typeof id !== 'number' || !Number.isFinite(id)) {
      return
    }
    if (attemptedFor === id) {
      return
    }

    const generation = ++ensureGeneration
    attemptedFor = id
    connectError.value = null

    try {
      const created = await ChatApiService.createEventChat({
        app_label: params.appLabel,
        model: params.modelName,
        object_id: id,
      })
      if (generation !== ensureGeneration) {
        return
      }
      chatId.value = created.id
    } catch (e) {
      if (generation !== ensureGeneration) {
        return
      }
      debugConsoleError('[useEntityChat] createEventChat error:', e)
      chatId.value = null
      if (isForbiddenError(e)) {
        connectError.value = ACCESS_DENIED_MESSAGE
      } else {
        attemptedFor = null
      }
    }
  }

  watch(
    [() => params.isEnabled.value, () => params.objectId.value],
    async ([enabled, objectId], oldValues) => {
      const prevObjectId = oldValues ? oldValues[1] : undefined
      if (objectId !== prevObjectId) {
        chatId.value = null
        connectError.value = null
        attemptedFor = null
        ensureGeneration += 1
      }
      if (!enabled || !isChatMatrixEnabled()) {
        return
      }
      if (typeof objectId !== 'number' || !Number.isFinite(objectId)) {
        return
      }
      await ensureChat()
    },
    { immediate: true },
  )

  function hideChat() {
    isChatVisible.value = false
  }

  function showChat() {
    isChatVisible.value = true
    void ensureChat()
  }

  async function openInSlidingChat() {
    await ensureChat()
    const id = chatId.value
    if (!id) {
      return
    }

    await openChat(null, id)
  }

  return {
    isAvailable,
    chatId,
    connectError,
    isChatVisible,
    hideChat,
    showChat,
    openInSlidingChat,
  }
}
